import type { ConnectionPositionPair } from '@angular/cdk/overlay';
import { CommonModule } from '@angular/common';
import {
  ChangeDetectorRef,
  Component,
  ViewChild,
  inject,
  input,
  ChangeDetectionStrategy,
} from '@angular/core';

import { Actions, ofType } from '@ngrx/effects';
import { Store } from '@ngrx/store';
import { switchMap, take, withLatestFrom } from 'rxjs';

import { Ability } from '@core/abilities-types';
import { CurrentUserSelectors } from '@core/account-data';
import { filterBoolean, filterDefined, trackByProp } from '@core/common-legacy';
import type { LocationJob } from '@core/locations-data';
import { LocationActions, LocationJobsActions, LocationJobsSelectors } from '@core/locations-data';
import { AssignedLocationsInfoContainerComponent } from '@core/locations-smart-ui';
import { injectOpenJobEditor } from '@core/offers-logic';
import { JobEditorDialogTab } from '@core/offers-types';
import { OfferStatusIndicatorComponent } from '@core/offers-ui';
import {
  ConnectedOverlayDirective,
  DropdownComponent,
  DropdownItemComponent,
  DropdownWidth,
  IconComponent,
  OverlayOriginDirective,
  SpinnerComponent,
} from '@core/user-interface-legacy';

type LocationJobItem = Pick<LocationJob, 'id' | 'status' | 'title' | 'slug' | 'locationIds'> & {
  hasAccess: boolean;
};

@Component({
  selector: 'rt-locations-jobs-dropdown-container',
  imports: [
    CommonModule,
    IconComponent,
    DropdownComponent,
    DropdownItemComponent,
    ConnectedOverlayDirective,
    OverlayOriginDirective,
    SpinnerComponent,
    OfferStatusIndicatorComponent,
    AssignedLocationsInfoContainerComponent,
  ],
  styleUrls: ['./locations-jobs-dropdown-container.component.less'],
  changeDetection: ChangeDetectionStrategy.Eager,
  templateUrl: './locations-jobs-dropdown-container.component.html',
})
export class LocationsJobsDropdownContainerComponent {
  private readonly store = inject(Store);
  private readonly actions$ = inject(Actions);
  private readonly cdRef = inject(ChangeDetectorRef);
  private readonly navigateToJobEditor = injectOpenJobEditor();

  public readonly id = input.required<number>();
  public readonly activeOffersCount = input<number>(undefined!);
  public readonly dropdownPositions = input<ConnectionPositionPair[]>(undefined!);

  @ViewChild(ConnectedOverlayDirective) public dropdown: ConnectedOverlayDirective;

  public locationJobItems: LocationJobItem[] = [];
  public trackById = trackByProp<{ id: number }>({ prop: 'id' });
  public DropdownWidth = DropdownWidth;
  public jobsDropdownLoading = false;

  public fetchLocationJobs(): void {
    this.jobsDropdownLoading = true;
    this.store.dispatch(LocationJobsActions.fetchOne({ payload: { id: this.id() } }));

    this.actions$
      .pipe(
        ofType(LocationJobsActions.fetchOneSuccess),
        take(1),
        switchMap(() => this.store.select(LocationJobsSelectors.byId(this.id()))),
        filterBoolean,
        take(1),
        withLatestFrom(this.store.select(CurrentUserSelectors.data).pipe(filterDefined)),
      )
      .subscribe(([locationJobs, currentUser]) => {
        const activeOffersCount = this.activeOffersCount();
        const refreshLocation = activeOffersCount && locationJobs.length !== activeOffersCount;
        this.locationJobItems = locationJobs.map(job => {
          const { id, status, title, slug, locationIds } = job;
          return {
            id,
            status,
            title,
            slug,
            locationIds,
            hasAccess:
              currentUser.roleAbilities.includes(Ability.ViewAllJobs) ||
              !!currentUser.membership?.offerIds.includes(job.id),
          };
        });
        if (refreshLocation)
          this.store.dispatch(LocationActions.fetchOne({ payload: { id: this.id() } }));
        this.jobsDropdownLoading = false;
        this.cdRef.detectChanges();
        this.dropdown.updatePositionWithoutRedrawing();
      });
  }

  public openJobEditor(jobId: number): void {
    this.navigateToJobEditor(jobId, JobEditorDialogTab.JobDetails);
  }
}
